<template>
  <div class="ad-banner-form">
    <div class="section-card-header">
      <h2>{{ isEdit ? 'Edycja banera' : 'Nowy baner' }}</h2>
      <p class="section-subtitle">
        Skonfiguruj grupy docelowe, wymiary oraz slajdy banera.
      </p>
    </div>

    <div class="form-grid">
      <label class="form-group">
        <span>Nazwa administracyjna</span>
        <input type="text" v-model="form.name" placeholder="Np. Promocja dla specjalistów">
      </label>

      <label class="form-group">
        <span>Szerokość banera</span>
        <input type="number" min="1" v-model.number="form.options.width">
      </label>

      <label class="form-group">
        <span>Umiejscowienie banera</span>
        <select v-model="form.placement_id">
          <option v-for="placement in placements" :value="placement.id" :key="placement.id">
            {{ placement.name }}
          </option>
        </select>
      </label>

      <label class="form-group">
        <span>Wysokość banera</span>
        <input type="number" min="1" v-model.number="form.options.height">
      </label>

      <label class="checkbox-row">
        <input type="checkbox" v-model="form.is_active">
        <span>Baner aktywny</span>
      </label>
    </div>

    <div class="target-groups">
      <span class="field-label">Grupy docelowe</span>

      <label class="checkbox-row">
        <input type="checkbox" value="clients" v-model="form.target_groups">
        <span>Klienci</span>
      </label>

      <label class="checkbox-row">
        <input type="checkbox" value="specialists" v-model="form.target_groups">
        <span>Specjaliści</span>
      </label>
    </div>

    <div class="slides-header">
      <div class="position-relative">
        <div class="slides-header-title">
          <h3>Slajdy</h3>
          <HintComponent page-key="admin_panel_ad_banners_create_slides"/>
        </div>
        <p>Każdy slajd musi mieć tekst oraz tło: kolor, gradient albo obrazek.</p>
      </div>
      <button class="btn-green" type="button" @click="addSlide">+ Dodaj slajd</button>
    </div>

    <div v-if="form.slides.length === 0" class="empty-state">
      <p>Dodaj co najmniej jeden slajd.</p>
    </div>

    <div
        v-for="(slide, index) in form.slides"
        :key="slide.localKey"
        class="slide-card"
    >
      <div class="slide-card__head">
        <h4>Slajd #{{ index + 1 }}</h4>
        <button class="btn-danger-outline" type="button" @click="removeSlide(index)">
          Usuń slajd
        </button>
      </div>

      <div class="form-grid">
        <label class="form-group">
          <span>Tytuł</span>
          <input type="text" v-model="slide.title" placeholder="Tytuł slajdu">
        </label>

        <label class="form-group">
          <span>Kolejność</span>
          <input type="number" min="0" v-model.number="slide.order">
        </label>

        <label class="checkbox-row">
          <input type="checkbox" v-model="slide.is_active">
          <span>Slajd aktywny</span>
        </label>
      </div>

      <label class="form-group mt-3">
        <span>Tekst slajdu</span>
        <textarea rows="3" v-model="slide.text" placeholder="Treść widoczna na banerze"></textarea>
      </label>

      <div class="form-grid">
        <label class="form-group">
          <span>Tekst przycisku CTA</span>
          <input type="text" v-model="slide.button_text" placeholder="Np. Sprawdź">
        </label>

        <label class="form-group">
          <span>Adres przycisku CTA</span>
          <input type="url" v-model="slide.button_url" placeholder="https://example.com">
        </label>
      </div>

      <div class="background-settings">
        <label class="form-group">
          <span>Typ tła</span>
          <select v-model="slide.options.background.type">
            <option value="color">Jednolity kolor</option>
            <option value="gradient">Gradient</option>
            <option value="image">Obrazek</option>
          </select>
        </label>

        <label
            v-if="slide.options.background.type === 'color'"
            class="form-group"
        >
          <span>Kolor tła</span>
          <input type="color" v-model="slide.options.background.color">
        </label>

        <label
            v-if="slide.options.background.type === 'gradient'"
            class="form-group"
        >
          <span>CSS gradient</span>
          <input
              type="text"
              v-model="slide.options.background.color"
              placeholder="linear-gradient(90deg, #10b981 0%, #f97316 100%)"
          >
        </label>

        <label
            v-if="slide.options.background.type === 'image'"
            class="form-group"
        >
          <span>Ścieżka obrazka</span>
          <input
              type="text"
              v-model="slide.options.background.image_path"
              placeholder="/upload/ad-banners/..."
          >
        </label>
      </div>

      <div v-if="slide.options.background.type === 'image'" class="upload-box">
        <input type="file" accept="image/*" @change="event => setSlideImageFile(index, event)">
        <button
            class="btn-outline"
            type="button"
            :disabled="!form.id || !slide.id || !slide.imageFile"
            @click="uploadSlideImage(slide)"
        >
          Wyślij obrazek tła
        </button>
        <small v-if="!form.id || !slide.id">
          Upload obrazka będzie dostępny po zapisaniu banera i slajdu.
        </small>
      </div>

      <div class="slide-preview" :style="slidePreviewStyle(slide)">
        <div>
          <h5>{{ slide.title || 'Tytuł slajdu' }}</h5>
          <p>{{ slide.text || 'Podgląd tekstu slajdu' }}</p>
          <span v-if="slide.button_text">{{ slide.button_text }}</span>
        </div>
      </div>
    </div>

    <div class="form-actions">
      <router-link class="btn-outline" to="/admin-profile/ad-banners">Anuluj</router-link>
      <button class="btn-primary" type="button" @click="save">
        {{ isEdit ? 'Zapisz zmiany' : '+ Dodaj baner' }}
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import authHeader from '@/services/auth-header';
import { useAppStore } from '@/stores/app';
import HintComponent from "@/components/SpecialComponents/HintComponent.vue";

const defaultSlide = order => ({
  id: null,
  localKey: `${Date.now()}-${Math.random()}`,
  title: '',
  text: '',
  button_text: '',
  button_url: '',
  order,
  is_active: true,
  imageFile: null,
  options: {
    background: {
      type: 'color',
      color: '#10b981',
      image_path: null,
    },
  },
});

const defaultForm = () => ({
  id: null,
  placement_id: null,
  name: '',
  target_groups: ['clients'],
  is_active: true,
  options: {
    width: 1200,
    height: 300,
  },
  slides: [
    defaultSlide(1),
  ],
});

export default {
  name: 'AdBannerForm',
  components: {HintComponent},
  props: {
    initialBanner: {
      type: Object,
      default: null,
    },
  },
  setup() {
    return {
      appStore: useAppStore(),
    };
  },
  data() {
    return {
      form: defaultForm(),
      placements: []
    };
  },
  computed: {
    isEdit() {
      return !!this.form.id;
    },
  },
  created() {
    this.getPlacements();
  },
  watch: {
    initialBanner: {
      immediate: true,
      handler(banner) {
        if (banner) {
          this.form = this.normalizeBannerToForm(banner);
        }
      },
    },
  },
  methods: {
    async getPlacements() {
      try {
        const response = await axios.get('/admin/ad-banner-placements', authHeader());
        this.placements = response.data;
      } catch (error) {
        console.error('Error fetching placements:', error);
      }
    },
    save() {
      if (!this.validateForm()) {
        return;
      }

      const payload = this.preparePayload();

      const request = this.form.id
          ? axios.put(`/admin/ad-banners/${this.form.id}`, payload, authHeader())
          : axios.post('/admin/ad-banners', payload, authHeader());

      request.then(res => {
        const banner = res.data.data || res.data;

        this.appStore.setIsSuccess(this.form.id ? 'Baner został zaktualizowany' : 'Baner został dodany');
        this.$emit('saved', banner);
      }).catch(() => {
        this.appStore.setIsAlert('Nie udało się zapisać banera');
      });
    },
    validateForm() {
      if (!this.form.name) {
        this.appStore.setIsAlert('Podaj nazwę banera');
        return false;
      }

      if (!this.form.target_groups.length) {
        this.appStore.setIsAlert('Wybierz co najmniej jedną grupę docelową');
        return false;
      }

      if (!this.form.options.width || !this.form.options.height) {
        this.appStore.setIsAlert('Podaj poprawne wymiary banera');
        return false;
      }

      if (!this.form.slides.length) {
        this.appStore.setIsAlert('Dodaj co najmniej jeden slajd');
        return false;
      }

      const invalidSlide = this.form.slides.find(slide => !slide.title || !slide.text);

      if (invalidSlide) {
        this.appStore.setIsAlert('Każdy slajd musi mieć tytuł i tekst');
        return false;
      }

      return true;
    },
    preparePayload() {
      return {
        placement_id: this.form.placement_id,
        name: this.form.name,
        target_groups: this.form.target_groups,
        is_active: this.form.is_active,
        options: {
          width: Number(this.form.options.width),
          height: Number(this.form.options.height),
        },
        slides: this.form.slides.map(slide => ({
          id: slide.id || undefined,
          title: slide.title,
          text: slide.text,
          button_text: slide.button_text || null,
          button_url: slide.button_url || null,
          order: Number(slide.order),
          is_active: slide.is_active,
          options: {
            background: {
              type: slide.options.background.type,
              color: slide.options.background.type === 'image' ? null : slide.options.background.color,
              image_path: slide.options.background.type === 'image' ? slide.options.background.image_path : null,
            },
          },
        })),
      };
    },
    normalizeBannerToForm(banner) {
      return {
        id: banner.id,
        placement_id: banner.placement_id,
        name: banner.name || '',
        target_groups: banner.target_groups || [],
        is_active: !!banner.is_active,
        options: {
          width: banner.options && banner.options.width ? banner.options.width : 1200,
          height: banner.options && banner.options.height ? banner.options.height : 300,
        },
        slides: (banner.slides || []).map((slide, index) => ({
          id: slide.id,
          localKey: `${slide.id}-${Date.now()}`,
          title: slide.title || '',
          text: slide.text || '',
          button_text: slide.button_text || '',
          button_url: slide.button_url || '',
          order: slide.order || index + 1,
          is_active: !!slide.is_active,
          imageFile: null,
          options: {
            background: {
              type: slide.options && slide.options.background ? slide.options.background.type : 'color',
              color: slide.options && slide.options.background ? slide.options.background.color : '#10b981',
              image_path: slide.options && slide.options.background ? slide.options.background.image_path : null,
            },
          },
        })),
      };
    },
    addSlide() {
      this.form.slides.push(defaultSlide(this.form.slides.length + 1));
    },
    removeSlide(index) {
      this.form.slides.splice(index, 1);
      this.form.slides = this.form.slides.map((slide, slideIndex) => ({
        ...slide,
        order: slideIndex + 1,
      }));
    },
    setSlideImageFile(index, event) {
      this.form.slides[index].imageFile = event.target.files && event.target.files[0] ? event.target.files[0] : null;
    },
    uploadSlideImage(slide) {
      if (!this.form.id || !slide.id || !slide.imageFile) {
        this.appStore.setIsAlert('Najpierw zapisz baner i wybierz plik');
        return;
      }

      const formData = new FormData();
      formData.append('image', slide.imageFile);

      axios.post(
          `/admin/ad-banners/${this.form.id}/slides/${slide.id}/background-image`,
          formData,
          {
            ...authHeader(),
            headers: {
              ...authHeader().headers,
              'Content-Type': 'multipart/form-data',
            },
          }
      ).then(res => {
        slide.options.background.type = 'image';
        slide.options.background.color = null;
        slide.options.background.image_path = res.data.image_path;
        slide.imageFile = null;
        this.appStore.setIsSuccess('Obrazek tła został przesłany');
      }).catch(() => {
        this.appStore.setIsAlert('Nie udało się przesłać obrazka');
      });
    },
    slidePreviewStyle(slide) {
      const background = slide.options.background;

      if (background.type === 'image' && background.image_path) {
        return {
          backgroundImage: `linear-gradient(rgba(17, 24, 39, .35), rgba(17, 24, 39, .35)), url("${process.env.VUE_APP_STORAGE_URL}${background.image_path}")`,
        };
      }

      if (background.type === 'gradient') {
        return {
          background: background.color || 'linear-gradient(90deg, #10b981 0%, #f97316 100%)',
        };
      }

      return {
        background: background.color || '#10b981',
      };
    },
  },
};
</script>

<style scoped lang="scss">
.ad-banner-form {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, .08);
  padding: 24px;
  box-sizing: border-box;
  width: 100%;
}

.section-card-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 18px;
}

.section-card-header h2 {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.section-subtitle,
.slides-header p {
  margin: 0;
  color: #6b7280;
  font-size: 1.4rem;
}

.slides-header-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
}

.form-group span,
.field-label {
  color: #374151;
  font-size: 1.35rem;
  font-weight: 600;
}

.form-group input,
.form-group textarea,
.form-group select {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 9px 11px;
  font-size: 1.4rem;
  outline: none;
  box-sizing: border-box;
  width: 100%;
  background: #fff;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  border-color: #10b981;
}

.checkbox-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.4rem;
  color: #374151;
  margin-bottom: 12px;
}

.checkbox-row input {
  accent-color: #10b981;
  width: 16px;
  height: 16px;
}

.target-groups {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 0;
  border-top: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 18px;
}

.slides-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.slides-header h3 {
  margin: 0 0 4px;
  color: #1f2937;
  font-size: 1.8rem;
}

.slide-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}

.slide-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
}

.slide-card__head h4 {
  margin: 0;
  color: #1f2937;
  font-size: 1.6rem;
}

.background-settings {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.upload-box {
  border: 1px dashed #d1d5db;
  border-radius: 10px;
  padding: 12px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.upload-box small {
  color: #6b7280;
  font-size: 1.25rem;
}

.slide-preview {
  min-height: 160px;
  border-radius: 10px;
  background-size: cover;
  background-position: center;
  color: #fff;
  display: grid;
  place-items: center;
  text-align: center;
  padding: 18px;
  box-sizing: border-box;
}

.slide-preview h5 {
  margin: 0 0 8px;
  font-size: 2rem;
}

.slide-preview p {
  margin: 0;
  font-size: 1.5rem;
}

.slide-preview span {
  display: inline-flex;
  margin-top: 12px;
  background: #f97316;
  border-radius: 999px;
  padding: 8px 16px;
  font-weight: 700;
  font-size: 1.3rem;
}

.empty-state {
  text-align: center;
  padding: 26px;
  color: #9ca3af;
  font-size: 1.5rem;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
  margin-bottom: 16px;
}

.form-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}

.btn-primary,
.btn-green,
.btn-outline,
.btn-danger-outline {
  border-radius: 8px;
  padding: 9px 15px;
  cursor: pointer;
  font-size: 1.4rem;
  font-weight: 600;
  text-decoration: none;
  display: inline-block;
  text-align: center;
}

.btn-primary {
  background: #f97316;
  color: #fff;
  border: none;
}

.btn-primary:hover {
  background: #ea6c0a;
}

.btn-green {
  background: #10b981;
  color: #fff;
  border: none;
}

.btn-green:hover {
  background: #059669;
}

.btn-outline {
  background: #fff;
  color: #1f2937;
  border: 1px solid #d1d5db;
}

.btn-outline:hover {
  border-color: #10b981;
  color: #10b981;
}

.btn-danger-outline {
  background: #fff;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.btn-danger-outline:hover {
  background: #fee2e2;
}

button:disabled {
  opacity: .55;
  cursor: not-allowed;
}

@media (max-width: 640px) {
  .ad-banner-form {
    padding: 16px 12px;
  }

  .form-grid,
  .background-settings {
    grid-template-columns: 1fr;
  }

  .slides-header,
  .form-actions {
    flex-direction: column;
  }

  .btn-primary,
  .btn-green,
  .btn-outline,
  .btn-danger-outline {
    width: 100%;
    text-align: center;
  }
}
</style>